import { configWithUpload } from '@/netadapters/AbsNetAdapter'
import { AuthAdapter } from '@/netadapters/AuthAdapter'
import type { Bean as DeviceBean } from '@core/devices/Device'
import type { AxiosResponse } from 'axios'
import type { ChannelInfo } from '@core/devices/IDevice'
import { DeviceType } from '@core/devices/IDevice'
import {
  HttpLocalCancelException,
  RequestTimeoutException,
  HttpServerException,
  HttpRemoteCancelException,
  UnrecoverableException,
} from '@core/Exceptions'
import { LoginStatus } from '@core/Constants'
import type { IFileTransmissionProgressListener } from '@core/FileTransmissionProgressListener'
import { DefaultFileTransmissionProgressListener, FileTransmissionError } from '@core/FileTransmissionProgressListener'
import type { HttpRequestCancelToken } from '@/core/HttpRequestCancelToken'
import sha256 from 'crypto-js/sha256'
import { getBackendApiGatewayBaseUrl, getBackendFileDownloadBaseUrl, getSmfServerApiBaseUrl } from '@/BuildConfig'
import { isEmpty, transmissionTagTracker } from '@core/Utils'
import { v4 as uuidV4 } from 'uuid'
import { switchDomainName } from '@/BuildUtils'
import {
  DOWNLOAD_FILE_CHUNK_SIZE,
  LAN_DOWNLOAD_FILE_CHUNK_SIZE,
  WAN_DOWNLOAD_MAX_SIZE_SINGLE_FILE_IN_BYTES,
} from '@/core/FileUtils'
import { TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES, retry } from '@/core/decorators/RetryDecorator'

const QUERY_TICKET = '?ticket='
const CODE_SUCCEED = 0

const DEVICE_TYPE_MAP = new Map<string, DeviceType>([
  ['FOLDPHONE', DeviceType.PHONE],
  ['TV', DeviceType.TV],
  ['WATCH', DeviceType.WATCH],
  ['PAD', DeviceType.PAD],
  ['MOBILE', DeviceType.PHONE],
])

const AUTH_COOKIE_INVALID = 1003

const AUTH_COOKIE_NEEDED = 1004

export enum DEVICE_PULL_UP_TYPE {
  SUCCESS = 0,
  NO_DEVICE = 4003,
  UNOPENED_OWORK = 11001,
  WRONG_PASSWORD = 11002,
  CONNECTED_TIMEOUT = 11003,
  UNSET_PASSWORD = 11004,
  NORMAL_ERROR = -1,
  AUTH_TOKEN_INVALID = 1003,
  AUTH_TOKEN_NEEDED = 1004,
}

export const DEVICE_PULL_UP_TIPS_MAP = new Map<DEVICE_PULL_UP_TYPE, string>([
  [DEVICE_PULL_UP_TYPE.SUCCESS, ''],
  [DEVICE_PULL_UP_TYPE.NO_DEVICE, 'owork_base.pull_device.device_pull_up_type_no_device'],
  [DEVICE_PULL_UP_TYPE.UNOPENED_OWORK, 'owork_base.pull_device.device_pull_up_type_unopened_owork'],
  [DEVICE_PULL_UP_TYPE.WRONG_PASSWORD, 'owork_base.pull_device.device_pull_up_type_wrong_password'],
  [DEVICE_PULL_UP_TYPE.CONNECTED_TIMEOUT, 'owork_base.pull_device.device_pull_up_type_connected_timeout'],
  [DEVICE_PULL_UP_TYPE.UNSET_PASSWORD, 'owork_base.pull_device.device_pull_up_type_unset_password'],
  [DEVICE_PULL_UP_TYPE.NORMAL_ERROR, 'owork_base.pull_device.device_pull_up_type_normal_error'],
])

// API list start
const API_DEVICE_LIST = '/web/userDevice/v1/devicesInfo'
const API_DEVICE_ACCESS_TOKEN = '/web/userDevice/v1/access-token'
const API_USER_INFO = '/web/account/v1/userInfo'
const API_NOTIFY_IDENTIFY_INFO = '/web/common/next-notify-id'
const API_USER_TICKET = '/callback/account/v1/ticket'
const API_USER_LOGOUT = '/web/account/v3/logout'
// 单文件上传预初始化
const API_SINGLE_FILE_UPLOAD = '/web/file-upload/server-encrypt-single-file-upload'
// 初始化分片信息
const API_MULTIPART_FILE_UPLOADING_INIT = '/web/file-upload/server-encrypt-multi-part-file-upload-init'
// 上传分片文件
const API_MULTIPART_FILE_UPLOADING_PART = '/web/file-upload/server-encrypt-multi-part-file-upload'
// 取消分片上传
const API_MULTIPART_FILE_UPLOADING_CANCEL = '/web/file-upload/multi-part-file-upload-cancel'
// 分片上传完成
const API_MULTIPART_FILE_UPLOADING_COMPLETE = '/web/file-upload/multi-part-file-upload-complete'
// 获取分片MetaInfo
const API_MULTIPART_FILE_UPLOADING_MULTI_PART = '/web/file-upload/multi-part-file-meta'

const API_REFRESH_TOKEN = '/web/account/v3/refresh-code'

// API list end

const API_GET_FILE_FROM_OCS = 'web/file-upload/common-file-download'

const DOWNLOAD_FILE_ID = 'fileId'
const DOWNLOAD_FILE_TYPE = 'contentInLine'

const API_PULL_UP_DEVICE = '/web/userDevice/v1/pull-up-device'
const API_CHECK_DEVICE_CONFIG = '/web/device-config/v1/device-switch-info'

const API_VALIDATE_CLIENT_TICKET = '/web/account/v3/deviceValidateTicket'

// 获取证书token以及非项目网页下载证书的链接地址
const API_GET_CERTIFICATE_TOKEN = '/web/getCertificate/token'

export const PATH_DOWNLOAD_CERTIFICATE_SERVICE = `/web/downloadCertificate/token`

/**
 * Used for all network request between OWork frontend and backend
 */
export class CloudAdapter extends AuthAdapter<BaseResponse | Blob> {
  constructor() {
    super()
    this._initInstance()
  }

  static validateResult(result: unknown) {
    return result !== undefined
  }

  protected _isInvalidAuth(res: AxiosResponse<BaseResponse>): boolean {
    return res.data.code !== undefined && [AUTH_COOKIE_INVALID, AUTH_COOKIE_NEEDED].includes(res.data.code)
  }

  protected _getBaseUrl() {
    return getBackendApiGatewayBaseUrl()
  }

  async getDeviceList(): Promise<DeviceBean[] | undefined> {
    try {
      const res = await this._instance.get<unknown, AxiosResponse<DeviceListResponse>>(API_DEVICE_LIST)

      if (this._isSuccess(res)) {
        return res.data.data.deviceInfos.map<DeviceBean>(DeviceInfoConverter)
      }
    } catch (error) {
      return undefined
    }
  }

  async getCertificateToken() {
    try {
      const res = await this._instance.post<unknown, AxiosResponse<CommonStringResponse>>(
        API_GET_CERTIFICATE_TOKEN,
        {},
        {
          baseURL: getSmfServerApiBaseUrl(),
        }
      )

      console.log(`[Certificate]: getCertificateToken  ${new Date().toLocaleString()}`, res)

      if (this._isSuccess(res)) {
        return res.data.data
      }
      console.log(`[Certificate]: getCertificateToken error  ${new Date().toLocaleString()}`, res)
    } catch (error) {
      console.log(`[Certificate]: getCertificateToken error  ${new Date().toLocaleString()}`, error)

      return undefined
    }
  }

  /**
   * 获取安全框架给与指定设备的调用设备端服务的访问凭证
   */
  async getDeviceAccessToken(deviceId: string): Promise<string | undefined> {
    try {
      const res = await this._instance.get<unknown, AxiosResponse<CommonStringResponse>>(API_DEVICE_ACCESS_TOKEN, {
        params: { deviceId },
      })

      if (this._isSuccess(res)) {
        return res.data.data
      }
    } catch (error) {
      return undefined
    }
  }

  async getUserInfo(): Promise<UserInfo | undefined> {
    try {
      const res = await this._instance.get<unknown, AxiosResponse<RawUserInfoResponse>>(API_USER_INFO)

      if (this._isSuccess(res)) {
        return {
          userName: res.data.data?.userName,
          sessionId: res.data.data?.qtUserId,
          ssoId: res.data.data?.ssoId,
          avatar: res.data.data?.avatar,
          loginStatus: LoginStatus.LOGIN_IS_SUCCESS,
          isTrustedClient: res.data.data.trustedDevice,
        }
      }
    } catch (error) {
      if (error instanceof RequestTimeoutException || error instanceof HttpServerException) {
        return {
          loginStatus: LoginStatus.TIME_OUT,
        }
      } else {
        return undefined
      }
    }
  }

  async setUserTicket(ticket: string): Promise<UserInfo | undefined> {
    try {
      const res = await this._instance.get<unknown, AxiosResponse<UserInfoResponse>>(
        `${API_USER_TICKET}${QUERY_TICKET}${ticket}`
      )

      if (this._isSuccess(res)) {
        return {
          loginStatus: LoginStatus.LOGIN_IS_SUCCESS,
        }
      }
    } catch (error) {
      return undefined
    }
  }

  async refreshAccessToken(): Promise<boolean> {
    try {
      const res = await this._instance.post<unknown, AxiosResponse<RefreshTokenResponse>>(API_REFRESH_TOKEN)

      return this._isSuccess(res)
    } catch (error) {
      console.error('refresh token error:', error)
    }

    return false
  }

  async isCertificatedClient(data: ValidateClientParams): Promise<boolean> {
    try {
      const res = await this._instance.post<unknown, AxiosResponse<ClientValidateTicketResult>>(
        API_VALIDATE_CLIENT_TICKET,
        data
      )

      return res.data.code === 0
    } catch (error) {
      return false
    }
  }

  async logout(): Promise<boolean | undefined> {
    try {
      const res = await this._instance.get<unknown, AxiosResponse<BaseResponse>>(API_USER_LOGOUT)

      return this._isSuccess(res)
    } catch (error) {
      return undefined
    }
  }

  async getNoticeIdentity(): Promise<string | undefined> {
    try {
      const res = await this._instance.get<unknown, AxiosResponse<CommonStringResponse>>(
        API_NOTIFY_IDENTIFY_INFO + `?${uuidV4()}`
      )

      if (this._isSuccess(res)) {
        return res.data.data
      }
    } catch (error) {
      console.error(error)

      return undefined
    }
  }

  /**
   * 单文件上传云端
   *
   * @param data 请求body
   * @param progressListener {@link IFileTransmissionProgressListener}
   * @param requestCancelToken used to cancel uploading
   * @returns
   */
  @retry(CloudAdapter.validateResult, undefined, TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES)
  async uploadFile(
    data: FormData,
    progressListener?: IFileTransmissionProgressListener,
    requestCancelToken?: HttpRequestCancelToken
  ): Promise<BaseResponse | undefined> {
    try {
      const res = await this._instance.post<unknown, AxiosResponse<CommonStringResponse>>(
        API_SINGLE_FILE_UPLOAD,
        data,
        {
          ...configWithUpload,
          onUploadProgress: (event: ProgressEvent) => {
            progressListener?.onProgressChanged(event.loaded, event.total)
          },
          signal: requestCancelToken?.signal,
        }
      )

      if (this._isSuccess(res)) {
        progressListener?.onCompleted(res.data.data)

        return res.data
      }
    } catch (error) {
      console.log(
        `[Transmission] uploadFile single ${data.get('fileName')} ${new Date().toLocaleString()} `,
        'error:',
        error
      )

      return undefined
    }
  }

  async downloadFile(
    fileInfo: {
      url: string
      size: number
      name: string
    },
    progressListener: IFileTransmissionProgressListener,
    requestCancelToken?: HttpRequestCancelToken,
    isLan?: boolean,
    isSupportRange?: boolean
  ): Promise<string | undefined> {
    console.log(`[Transmission]: downloadFile fileUrl ${new Date().toLocaleString()}`, fileInfo.url)
    let blobs: Blob[] | undefined
    const downloadProgressListener = new DefaultFileTransmissionProgressListener(progressListener)

    try {
      if (fileInfo.size < WAN_DOWNLOAD_MAX_SIZE_SINGLE_FILE_IN_BYTES || (isLan && !isSupportRange)) {
        console.log(
          `[Transmission]: downloadSingleFile isLan:${isLan} fileName: ${fileInfo.name} ${new Date().toLocaleString()}`
        )

        blobs = await this.downloadSingleFile(fileInfo, downloadProgressListener, requestCancelToken)
      } else {
        console.log(`[Transmission]: downloadFileMultipart fileName: ${fileInfo.name} ${new Date().toLocaleString()}`)

        blobs = await this.#downloadFileMultipart(fileInfo, downloadProgressListener, requestCancelToken, isLan)
      }

      if (blobs) {
        // 处理objectURl
        const fileObjectUrl = URL.createObjectURL(new Blob(blobs))

        console.log(
          `[Transmission]: downloadFile fileName: ${fileInfo.name}  ${new Date().toLocaleString()} fileObjectUrl:`,
          fileObjectUrl
        )

        downloadProgressListener.onCompleted(fileObjectUrl)

        return fileObjectUrl
      }
      downloadProgressListener.onError(FileTransmissionError.UNKNOWN)
    } catch (error) {
      console.log(`[Transmission]:  error fileName: ${fileInfo.name} ${new Date().toLocaleString()}`, error)

      if (error instanceof HttpLocalCancelException) {
        downloadProgressListener.onCancelled(false)
      } else if (error instanceof HttpRemoteCancelException) {
        downloadProgressListener.onCancelled(true)
      } else {
        downloadProgressListener.onError(FileTransmissionError.UNKNOWN)
      }

      return undefined
    }
  }

  /**
   * 分片上传初始化
   * @param data 请求参数
   * @param requestCancelToken used to cancel request
   * @returns
   */
  @retry(CloudAdapter.validateResult, undefined, TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES)
  async initFileMultipartUploading(
    data: UploadFileWithMultipartParams,
    requestCancelToken?: HttpRequestCancelToken
  ): Promise<UploadPartFileInitResponse | undefined> {
    const tag = transmissionTagTracker.takeTag()

    try {
      const res = await this._instance.post<unknown, AxiosResponse<UploadPartFileInitResponse>>(
        API_MULTIPART_FILE_UPLOADING_INIT,
        data,
        {
          signal: requestCancelToken?.signal,
          baseURL: switchDomainName(this._getBaseUrl(), tag),
        }
      )

      transmissionTagTracker.returnTag(tag)

      if (this._isSuccess(res)) {
        return res.data
      }
      console.log(
        `[Transmission] initFileMultipartUploading fileName: ${data.fileName} ${new Date().toLocaleString()} `,
        'error:',
        res
      )
    } catch (error) {
      transmissionTagTracker.returnTag(tag)

      console.log(
        `[Transmission] initFileMultipartUploading fileName: ${data.fileName} ${new Date().toLocaleString()} `,
        'error:',
        error
      )

      return undefined
    }
  }

  /**
   * 上传分片到指定的session id
   *
   * @param multipartId 文件分片上传的标识
   * @param fileChunk 文件块
   * @param chunkNo 文件块序号
   * @param progressListener {@link IFileTransmissionProgressListener}
   * @param requestCancelToken used to cancel request
   * @returns
   */
  @retry(CloudAdapter.validateResult, undefined, TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES)
  async uploadFileMultipartById(
    multipartId: string,
    fileChunk: Blob,
    chunkNo: number,
    progressListener?: IFileTransmissionProgressListener,
    requestCancelToken?: HttpRequestCancelToken,
    name?: string
  ): Promise<BaseResponse | undefined> {
    const formData = new FormData()

    formData.append('multipartId', multipartId)
    formData.append('file', fileChunk)
    formData.append('partNumber', chunkNo.toString())
    const tag = transmissionTagTracker.takeTag()

    try {
      const res = await this._instance.post<unknown, AxiosResponse<BaseResponse>>(
        API_MULTIPART_FILE_UPLOADING_PART,
        formData,
        {
          ...configWithUpload,
          onUploadProgress: (event: ProgressEvent) => {
            progressListener?.onProgressChanged(event.loaded, event.total)
          },
          signal: requestCancelToken?.signal,
          baseURL: switchDomainName(this._getBaseUrl(), tag),
        }
      )

      transmissionTagTracker.returnTag(tag)
      console.log(
        `[Transmission] uploadFileMultipartById: ${multipartId} fileName: ${name}  index: ${chunkNo} ${new Date().toLocaleString()} `,
        'res:',
        res
      )

      if (this._isSuccess(res)) {
        return res.data
      }
      console.log(
        `[Transmission] uploadFileMultipartById: ${multipartId} fileName: ${name}  index: ${chunkNo} ${new Date().toLocaleString()} `,
        'error:',
        res
      )
    } catch (error) {
      transmissionTagTracker.returnTag(tag)
      console.log(
        `[Transmission] uploadFileMultipartById: ${multipartId} fileName: ${name} index: ${chunkNo} ${new Date().toLocaleString()} `,
        'error:',
        error
      )

      if (error instanceof HttpLocalCancelException) {
        progressListener?.onCancelled(false)
        throw new UnrecoverableException()
      } else if (error instanceof HttpRemoteCancelException) {
        progressListener?.onCancelled(true)
        throw new UnrecoverableException()
      }

      return undefined
    }
  }

  /**
   * 分片上传完成
   * @param multipartId 初始化时获取的ID
   * @returns
   */
  @retry(CloudAdapter.validateResult, undefined, TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES)
  async completeFileMultipartUploading(multipartId: string): Promise<UploadPartFileCompleteResponse | undefined> {
    const tag = transmissionTagTracker.takeTag()

    try {
      const res = await this._instance.post<unknown, AxiosResponse<UploadPartFileCompleteResponse>>(
        API_MULTIPART_FILE_UPLOADING_COMPLETE,
        {
          multiPartId: multipartId,
        },
        { baseURL: switchDomainName(this._getBaseUrl(), tag) }
      )

      transmissionTagTracker.returnTag(tag)

      if (this._isSuccess(res)) {
        return res.data
      }
      console.log(
        `[Transmission] completeFileMultipartUploading: ${multipartId}   ${new Date().toLocaleString()} `,
        'error:',
        res
      )
    } catch (error) {
      transmissionTagTracker.returnTag(tag)

      console.log(
        `[Transmission] completeFileMultipartUploading: ${multipartId}   ${new Date().toLocaleString()} `,
        'error:',
        error
      )

      return undefined
    }
  }

  /**
   * 获取分片MetaInfo
   * @param multipartId 初始化时获取的ID
   * @returns
   */
  async getFileMultiPartInfoById(
    multipartId: string,
    requestCancelToken?: HttpRequestCancelToken
  ): Promise<UploadPartFileMetaInfoResponse | undefined> {
    try {
      const res = await this._instance.get<unknown, AxiosResponse<UploadPartFileMetaInfoResponse>>(
        API_MULTIPART_FILE_UPLOADING_MULTI_PART,
        {
          params: { multiPartId: multipartId },
          signal: requestCancelToken?.signal,
        }
      )

      if (this._isSuccess(res)) {
        return res.data
      }
    } catch (error) {
      return undefined
    }
  }

  /**
   * 取消分片上传
   * @param multipartId 初始化时获取的ID
   * @returns
   */
  async cancelFileMultipartUploadingById(multipartId: string): Promise<BaseResponse | undefined> {
    try {
      const res = await this._instance.post<unknown, AxiosResponse<BaseResponse>>(API_MULTIPART_FILE_UPLOADING_CANCEL, {
        params: {
          multipartId,
        },
      })

      if (this._isSuccess(res)) {
        return res.data
      }
    } catch (error) {
      return undefined
    }
  }

  getDownloadUrl(fileId: string, tag?: string): string {
    const baseUrl = getBackendFileDownloadBaseUrl()
    const downloadBaseUrl = tag ? switchDomainName(baseUrl, tag) : baseUrl
    const url = new URL(downloadBaseUrl + API_GET_FILE_FROM_OCS)

    url.searchParams.append(DOWNLOAD_FILE_ID, fileId)
    url.searchParams.append(DOWNLOAD_FILE_TYPE, 'false')

    return url.toString()
  }

  async pullUpDevice(deviceId: string, type: string, password?: string): Promise<DevicePullUpResult | undefined> {
    let res: AxiosResponse<BaseResponse, any> | undefined = undefined
    const requestData: DevicePullUpRequestData = {
      deviceId,
      type,
    }

    try {
      if (!isEmpty(password)) {
        requestData.pushPassword = sha256(password + deviceId).toString()
      }

      res = await this._instance.post<unknown, AxiosResponse<BaseResponse>>(API_PULL_UP_DEVICE, requestData)
    } catch (error) {
      return undefined
    }

    // 拉取设备的接口返回格式和其他接口不同，需要直接返回状态码供后续业务提示使用，所以当前接口不使用_isSuccess判断成功状态
    return {
      data: res.data.code,
    }
  }

  async deviceConfigCheck(deviceId: string): Promise<DeviceConfigCheckResult | undefined> {
    let res: AxiosResponse<DeviceConfigCheckResult> | undefined = undefined

    try {
      res = await this._instance.post<unknown, AxiosResponse<DeviceConfigCheckResult>>(API_CHECK_DEVICE_CONFIG, {
        deviceId,
      })
    } catch (error) {
      return undefined
    }

    if (this._isSuccess(res)) {
      return res.data
    }
  }

  protected _isSuccess(response: AxiosResponse<BaseResponse>): boolean {
    return super._isSuccess(response) && response.data.code == CODE_SUCCEED
  }

  protected _isInvalidAuthToken = (code: DEVICE_PULL_UP_TYPE | undefined) => {
    if (code) {
      return [DEVICE_PULL_UP_TYPE.AUTH_TOKEN_INVALID, DEVICE_PULL_UP_TYPE.AUTH_TOKEN_NEEDED].includes(code)
    } else {
      return false
    }
  }

  protected _getWithCredentialsOption(): boolean {
    return true
  }

  async #downloadFileMultipart(
    fileInfo: {
      url: string
      size: number
    },
    progressListener: IFileTransmissionProgressListener,
    requestCancelToken?: HttpRequestCancelToken,
    isLan?: boolean
  ): Promise<Blob[] | undefined> {
    const CHUNK_SIZE = isLan ? LAN_DOWNLOAD_FILE_CHUNK_SIZE : DOWNLOAD_FILE_CHUNK_SIZE
    const chunkCount = Math.ceil(fileInfo.size / CHUNK_SIZE)

    const progressMap = new Map<number, number>()
    const results: AxiosResponse<Blob>[] = []

    for (const i of Array.from({ length: chunkCount }, (_, i) => i)) {
      const start = i * CHUNK_SIZE
      const end = Math.min((i + 1) * CHUNK_SIZE, fileInfo.size) - 1

      progressMap.set(i, 0)
      const result = await this.downloadSinglePart(
        fileInfo,
        progressListener,
        progressMap,
        start,
        end,
        i,
        requestCancelToken
      )

      results.push(result)
    }

    return results.every((res) => !!res.data) ? results.map((res) => res.data) : undefined
  }

  @retry(CloudAdapter.validateResult, undefined, TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES)
  private async downloadSinglePart(
    fileInfo: {
      url: string
      size: number
    },
    progressListener: IFileTransmissionProgressListener,
    progressMap: Map<number, number>,
    start: number,
    end: number,
    i: number,
    requestCancelToken?: HttpRequestCancelToken
  ) {
    return await this._instance.get<unknown, AxiosResponse<Blob>>(fileInfo.url, {
      ...configWithUpload,
      onDownloadProgress: (event: ProgressEvent) => {
        progressMap.set(i, event.loaded)

        progressListener?.onProgressChanged(event.loaded, fileInfo.size, progressMap)
      },
      headers: {
        Range: `bytes=${start}-${end}`,
      },
      signal: requestCancelToken?.signal,
      responseType: 'blob',
      baseURL: '',
    })
  }

  @retry(CloudAdapter.validateResult, undefined, TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES)
  private async downloadSingleFile(
    fileInfo: {
      url: string
      size: number
    },
    progressListener: IFileTransmissionProgressListener,
    requestCancelToken?: HttpRequestCancelToken
  ): Promise<Blob[] | undefined> {
    const res = await this._instance.get<unknown, AxiosResponse<Blob>>(fileInfo.url, {
      ...configWithUpload,
      onDownloadProgress: (event: ProgressEvent) => {
        progressListener.onProgressChanged(event.loaded, event.total)
      },
      signal: requestCancelToken?.signal,
      responseType: 'blob',
      baseURL: '',
    })

    if (super._isSuccess(res)) {
      return [res.data]
    }
    console.log(`[Transmission]: downloadSingleFile error ${new Date().toLocaleString()}`, res)
  }
}

export interface BaseResponse {
  code: number
  msg: string
}

interface DeviceInfo {
  deviceType: string
  model: string
  id: string
  channel: {
    lan?: ChannelInfo
    wan?: ChannelInfo
  }
  version: string
}

export interface UploadFileWithMultipartParams {
  businessId: string
  businessType: string
  parentDir: string
  fileMd5: string
  lastModified: number
  fileName: string
  fileSize: number
  partSize: number
  partNumber: number
}

// 初始化分片响应参数
interface UploadPartFileInitRespInfo {
  multiPartId: string
}

// 获取分片MetaInfo响应参数
export interface UploadPartFileMetaInfoResp {
  fileSize: number
  partSize: number
  totalPart: number
  fileStatus: string
  fileName: string
  maxPartNumber: number
}

interface BaseUserInfo {
  avatar?: {
    default: string | undefined
    personalizationDefault: string | undefined
  }
  userName?: string | undefined
  sessionId?: string
  qtUserId?: string
  ssoId?: string
}

export interface UserInfo extends BaseUserInfo {
  loginStatus?: string
  isTrustedClient?: boolean
}

interface RawUserInfo extends BaseUserInfo {
  trustedDevice?: boolean
}
interface RawUserInfoResponse extends BaseResponse {
  data: RawUserInfo
}

export interface ValidateClientParams {
  appId: string
  ticketNo: string
  businessId: string
}

export interface ClientValidateTicketResult extends BaseResponse {
  data: boolean | null
}

export interface DeviceInfoList {
  deviceInfos: DeviceInfo[]
}

interface DeviceListResponse extends BaseResponse {
  data: DeviceInfoList
}

interface UserInfoResponse extends BaseResponse {
  data: RawUserInfo
}

interface CommonStringResponse extends BaseResponse {
  data: string
}

// 初始化分片响应
interface UploadPartFileInitResponse extends BaseResponse {
  data: UploadPartFileInitRespInfo
}

// 分片上传完成响应
interface UploadPartFileCompleteResponse extends BaseResponse {
  data: string
}

// 获取分片MetaInfo
export interface UploadPartFileMetaInfoResponse extends BaseResponse {
  data: UploadPartFileMetaInfoResp
}

export interface DevicePullUpResult {
  data: DEVICE_PULL_UP_TYPE | undefined
}

export interface DeviceConfigCheckResult extends BaseResponse {
  data: DeviceConfigCheckRequestData
}

export interface DeviceConfigCheckRequestData {
  openOworkSwitch: boolean
  inConnectTime: boolean
}

interface DevicePullUpRequestData {
  deviceId: string
  pushPassword?: string
  type: string
}

export interface RefreshTokenResponse extends BaseResponse {
  data: refreshTokenResult
}

interface refreshTokenResult {
  expireInSeconds: number
}

export const DeviceInfoConverter = (item: DeviceInfo): DeviceBean => {
  let type = DEVICE_TYPE_MAP.get(item.deviceType)

  if (!type) {
    type = DeviceType.PHONE
  }

  return {
    model: item.model,
    id: item.id,
    type,
    channel: item.channel,
    version: item.version,
  }
}

export const INSTANCE = new CloudAdapter()
